/**
 * WSO2 configuration for a specific environment.
 */
interface WSO2Config {
  clientId: string; // WSO2 application client ID
  redirectUri: string; // OAuth callback URL
  apiPath: string; // API context path
}

/**
 * Deployment environment types.
 */
type Environment = 'development' | 'test' | 'production';

/**
 * WSO2 Configuration for Each Environment
 * 
 * USS OIDC library auto-discovers OAuth endpoints based on the environment.
 * We only need to provide:
 * - clientId: WSO2 application client ID (different per environment)
 * - redirectUri: OAuth callback URL
 * - apiPath: API context path for the class schedules API
 * 
 * Environment Detection:
 * - localhost / 127.0.0.1 → development (gateway.uapi.dev.utah.edu)
 * - *.app.dev.utah.edu → development (gateway.uapi.dev.utah.edu)
 * - *.app.test.utah.edu → test (gateway.uapi.dev.utah.edu - may change)
 * - *.app.utah.edu → production (gateway.uapi.utah.edu - different gateway!)
 * 
 * USS OIDC automatically detects the correct gateway URL based on the environment.
 * 
 * Public PKCE clients do not use a client secret in browser code.
 * Only the client ID and redirect URI are needed here.
 * 
 * TODO: Update client IDs when WSO2 admin provides them for each environment
 */
const WSO2_CONFIGS: Record<Environment, WSO2Config> = {
  development: {
    // Used for: localhost AND myclassmap.app.dev.utah.edu
    // Application ID: 26ed2aa3-841e-4d4f-b283-47eb4ea27ea2
    clientId: 'oY_YfDZ8grfY61qfk9dfDbSYSYEa',
    apiPath: '/myclassmapapi/1.0.0-dev',
    redirectUri: window.location.origin + '/',
  },
  test: {
    // Used for: myclassmap.app.test.utah.edu
    clientId: 'Kw2V_dfseebSUqGSOnnn9SuUvqEa',
    apiPath: '/myclassmapapi-test/1.0.0-test',
    redirectUri: window.location.origin + '/',
  },
  production: {
    // Used for: myclassmap.app.utah.edu
    // TODO: Confirm final production client ID with WSO2 admin
    clientId: 'NE9yLO_btZVYQh51KF0nanWCiZwa',
    apiPath: '/myclassmapapi/1.0.0',
    redirectUri: window.location.origin + '/',
  },
};

/**
 * Detects the current deployment environment based on hostname.
 * @returns The detected environment
 */
function detectEnvironment(): Environment {
  const hostname = window.location.hostname;
  
  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }
  
  // Development environment
  if (hostname.includes('myclassmap.app.dev.utah.edu')) {
    return 'development';
  }
  
  // Test environment
  if (hostname.includes('myclassmap.app.test.utah.edu')) {
    return 'test';
  }
  
  // Production environment
  if (
    hostname.includes('myclassmap.app.utah.edu') ||
    hostname.includes('myclassmap.utah.edu')
  ) {
    return 'production';
  }
  
  // Unknown hostname, default to development for safety
  console.warn(`Unknown hostname: ${hostname}, defaulting to development`);
  return 'development';
}

/**
 * Gets the WSO2 configuration for the current environment.
 * Auto-detects environment based on hostname.
 * @returns WSO2Config object with client ID, redirect URI, and API path
 */
export function getWSO2Config(): WSO2Config {
  const env = detectEnvironment();
  const config = WSO2_CONFIGS[env];
    
  return config;
}

/**
 * Gets the current deployment environment.
 * @returns The detected environment
 */
export function getCurrentEnvironment(): Environment {
  return detectEnvironment();
}
